import { XCHAIN_STAGES } from "@decibeltrade/utils";
import { useQuery } from "@tanstack/react-query";
import z from "zod/v4";

import { ERROR_BACKOFF_MS } from "@/contexts/queries/backoff";
import { env } from "@/env";
import { authenticatedRequest, type GetAuthHeaders } from "@/utils/signature-auth";

// Mirror of XchainWithdrawalSchema in typescript/server/src/routes/xchain.route.ts — keep the
// field list identical when the server DTO changes.
const XchainWithdrawalSchema = z.object({
  routeId: z.string(),
  depositAddress: z.string(),
  destinationAddress: z.string(),
  /**
   * The subaccount the funds left. Null on withdrawals quoted before it was recorded — the client
   * falls back to the selected account for those, which is what it used to do for all of them.
   */
  subaccountAddress: z.string().nullable(),
  stage: z.enum(XCHAIN_STAGES),
  status: z.string(),
  updatedAt: z.string().nullable(),
  amountInFormatted: z.string().nullable(),
  amountOutFormatted: z.string().nullable(),
  /**
   * Fixed by the quote, not the live status — unlike the other fields here this never changes
   * between a pending withdrawal and its settled one.
   */
  withdrawFeeFormatted: z.string().nullable(),
  refundedAmountFormatted: z.string().nullable(),
  refundReason: z.string().nullable(),
  originTxHash: z.string().nullable(),
  destinationTxHash: z.string().nullable(),
  createdAt: z.string(),
});

const WithdrawalsResponseSchema = z.discriminatedUnion("success", [
  z.object({
    success: z.literal(true),
    data: z.object({ withdrawals: z.array(XchainWithdrawalSchema) }),
  }),
  z.object({ success: z.literal(false), error: z.string() }),
]);

export type XchainWithdrawal = z.infer<typeof XchainWithdrawalSchema>;

/**
 * Scoped to the account whose SIWA session the server reads the withdrawals from, for the reason
 * `xchainDepositsQueryKey` gives: a wallet switch must not serve the previous account's records.
 */
export function xchainWithdrawalsQueryKey(accountAddress: string | undefined) {
  return ["xchainWithdrawals", accountAddress] as const;
}

/** Matches the deposits poll, and sits above the server's 3s status cache for the same reason. */
const POLL_INTERVAL_MS = 5_000;

/**
 * Whether anything is still moving.
 *
 * `waiting` counts here, unlike on deposits. A withdrawal quote is minted immediately before we
 * send to it, so a record that has not been picked up yet is a send in its first seconds — not an
 * address the user may never fund.
 */
function hasInFlightWithdrawal(withdrawals: XchainWithdrawal[] | undefined): boolean {
  return withdrawals?.some((w) => w.stage === "waiting" || w.stage === "pending") ?? false;
}

export function useXchainWithdrawalsQuery({
  accountAddress,
  getAuthHeaders,
  subaccountAddress,
  enabled = true,
}: {
  accountAddress: string | undefined;
  getAuthHeaders: GetAuthHeaders;
  subaccountAddress?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: xchainWithdrawalsQueryKey(accountAddress),
    queryFn: async (): Promise<XchainWithdrawal[]> => {
      const response = await authenticatedRequest.get({
        schema: WithdrawalsResponseSchema,
        url: `${env.NEXT_PUBLIC_API_URL}/api/xchain/withdrawals`,
        getAuthHeaders,
        subaccountAddress,
      });
      if (!response.data.success) {
        throw new Error(response.data.error);
      }
      return response.data.data.withdrawals;
    },
    enabled: enabled && Boolean(accountAddress) && Boolean(getAuthHeaders),
    refetchInterval: (query) => {
      if (query.state.status === "error") return ERROR_BACKOFF_MS;
      return hasInFlightWithdrawal(query.state.data) ? POLL_INTERVAL_MS : false;
    },
    // A withdrawal settles on the destination chain minutes after the user has moved on, so the
    // card has to keep advancing while the tab is in the background.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    retry: 2,
  });
}
